import {
  LightButton,
  Modal,
  ModalSizeEnum,
  PrimaryButton,
  SecondaryButton,
  toast,
} from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { updateMailingDoubleOptInSettings } from 'modules/profile/mailing-settings/api/mailing-settings-double-opt-in-api'
import { useMailingDoubleOptIn } from 'modules/profile/mailing-settings/hooks/use-mailing-double-optin'
import React, { useCallback, useEffect, useState } from 'react'
import DashboardMailEditor from 'shared/components/dashboard-mail-editor'
import { useUnsavedEmail } from 'shared/components/dashboard-mail-editor/hooks/use-unsaved-email'
import {
  AttachmentFileInterface,
  EmailBaseInterface,
  EmailErrorResponseContentInterface,
  EmailErrorType,
  UnsavedAttachmentFileInterface,
} from 'shared/components/dashboard-mail-editor/types/email-interface'
import { getMailingUpdateData } from 'shared/components/dashboard-mail-editor/utils/get-mailing-update-data'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { MailingDoubleOptInSettingsInterface } from '../types/mailing-settings-double-optin-interface'

const defaultErrors: EmailErrorType = {
  body: '',
  fromEmail: '',
  fromName: '',
  subject: '',
  previewText: '',
  common: [],
}

function DoubleOptInModal() {
  const { t } = useLocoTranslation()
  const [errors, setErrors] = useState<EmailErrorType>(defaultErrors)
  const [isFetching, setIsFetching] = useState(false)
  const [opened, setOpened] = useState(false)
  const [tempState, setTempState] = useState<EmailBaseInterface>()
  const [attachmentsTempState, setAttachmentsTempState] = useState<
    (AttachmentFileInterface | UnsavedAttachmentFileInterface)[]
  >([])
  const [shouldFetchDoubleOptIn, setShouldFetchDoubleOptIn] = useState(false)
  const { mailingDoubleOptInSettings, mutateOptInSettings } = useMailingDoubleOptIn({
    shouldFetch: shouldFetchDoubleOptIn,
  })

  const setEmail = useCallback((ruleEmail: MailingDoubleOptInSettingsInterface | undefined) => {
    if (ruleEmail) {
      setTempState(prev => {
        return {
          ...prev,
          ...ruleEmail.currentEmail,
        }
      })
      setAttachmentsTempState(ruleEmail.currentEmail.attachments)
    }
  }, [])

  useEffect(() => {
    setEmail(mailingDoubleOptInSettings)
  }, [mailingDoubleOptInSettings, setEmail])

  const handleConfirm = async () => {
    try {
      if (tempState) {
        const doubleOptInData = {
          currentEmail: getMailingUpdateData(
            {
              body: tempState.body,
              fromEmail: tempState.fromEmail,
              fromName: tempState.fromName,
              subject: tempState.subject,
              previewText: tempState.previewText,
            },
            attachmentsTempState,
          ),
        }
        setErrors(defaultErrors)
        setIsFetching(true)
        const response = await updateMailingDoubleOptInSettings(doubleOptInData)
        await mutateOptInSettings(data => {
          if (data) {
            return { ...response.data }
          }
        }, false)
        await removeDbRecord()
        onClose()
        toast.success(t('global.changes_saved'))
      }
    } catch (e) {
      setIsFetching(false)
      if (e instanceof BadRequest) {
        const mailingDoubleOptInSettingsErrors =
          e.errors as unknown as EmailErrorResponseContentInterface
        const fieldErrors = mailingDoubleOptInSettingsErrors.fields.currentEmail
        if (fieldErrors) {
          setErrors(prev => ({
            ...prev,
            body: fieldErrors.body?.join(''),
            fromEmail: fieldErrors.fromEmail?.join(''),
            fromName: fieldErrors.fromName?.join(''),
            subject: fieldErrors.subject?.join(''),
            previewText: fieldErrors.previewText?.join(''),
          }))
        }

        if (e.errors.common) {
          const commonErrors = e.errors.common
          setErrors(prev => {
            return {
              ...prev,
              common: commonErrors,
            }
          })
        }
      } else {
        toast.error(t('global.error'))
      }
    }
  }

  const onClose = () => {
    setOpened(false)
    setIsFetching(false)
  }

  const { UnsavedEmailConfirmationModal, removeDbRecord, setNeedToShowModal } = useUnsavedEmail({
    mailingId: mailingDoubleOptInSettings?.id,
    initialBody: mailingDoubleOptInSettings?.currentEmail.body,
    currentBody: tempState?.body,
    onPaste: data => setTempState(prev => prev && { ...prev, body: data }),
  })

  const onChangeEmail = (value: EmailBaseInterface) => setTempState(value)

  const onAfterLeave = () => {
    setEmail(mailingDoubleOptInSettings)
    setErrors(defaultErrors)
    setShouldFetchDoubleOptIn(false)
  }

  return (
    <>
      <LightButton
        className="bg-background-secondary border-text-secondary/30 border border-solid"
        onClick={() => {
          setOpened(true)
          setShouldFetchDoubleOptIn(true)
        }}
        isFetching={shouldFetchDoubleOptIn && !mailingDoubleOptInSettings}
      >
        {t('dashboard.settings.mailing.double_opt_in_edit_button_title')}
      </LightButton>
      <Modal
        opened={opened && !!mailingDoubleOptInSettings}
        onClose={onClose}
        afterEnter={() => setNeedToShowModal(true)}
        afterLeave={onAfterLeave}
        title={t('dashboard.settings.mailing.double_opt_in_edit_modal.title')}
        isFetching={isFetching}
        size={ModalSizeEnum.big}
      >
        <form className="flex flex-col gap-6 lg:gap-7">
          <DashboardMailEditor
            showSettingsLink={false}
            inModal
            isPreFetching={!mailingDoubleOptInSettings}
            tempState={tempState}
            onChangeEmail={onChangeEmail}
            attachmentsTempState={attachmentsTempState}
            setAttachmentsTempState={setAttachmentsTempState}
            errors={errors}
            additionalSubstitutes={[
              {
                name: t(
                  'dashboard.settings.mailing.double_opt_in_edit_modal.available_replacements_confirmation_link_title',
                ),
                slug: 'double_opt_in_confirmation_link',
              },
            ]}
          />
          <div className="flex justify-center gap-4">
            <SecondaryButton
              width="large"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault()
                onClose()
              }}
              type="submit"
            >
              {t('confirmation_modal.cancel')}
            </SecondaryButton>
            <PrimaryButton
              width="large"
              onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault()
                await handleConfirm()
              }}
              isFetching={isFetching}
              disabled={isFetching}
            >
              {t('global.save')}
            </PrimaryButton>
          </div>
        </form>
        {UnsavedEmailConfirmationModal}
      </Modal>
    </>
  )
}

export default DoubleOptInModal
